import * as React from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearParameters,
  removeParameter,
  setParameters,
} from "../../redux/global/actions";
import { useSearchParams } from "react-router-dom";
import Button from "@mui/material/Button";
import MultiValueInput from "../../inputs/multiValueInput";
import {
  Stack,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import "./MissingSnsStatus.css";
import {
  getMissingSnsStatus,
  getMissingSnsStatusBulk,
} from "../../redux/dynamo_db/actions";
import MultiSelector from "../../inputs/MultiSelector";
import {
  downloadCSV,
  downloadJSON,
  extractUserInfoFromToken,
  getLocalStorageSAMLToken,
} from "../../utils";
import { DataTable, MaterialSymbol } from "@datalake/towercontrol-banner";
import ExportMenu from "../../widgets/ExportMenu";

const MISSING_SNS_STATUS_COLUMNS = [
  { id: "serial_number", label: "Serial number", filter: "text", nowrap: true },
  { id: "created_at", label: "Created day", nowrap: true },
  { id: "sn_status", label: "Status", filter: "select" },
  { id: "part_number", label: "Part Number", filter: "text" },
  { id: "tag", label: "Missing detection location", filter: "text" },
];

export default function MissingSnsStatus() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const hasProcessedUrlParams = React.useRef(false);

  const queryParameters = useSelector(
    (state) => state?.parameters?.missingSnsStatus,
  );

  const [currentSerialNumber, setCurrentSerialNumber] = React.useState();

  const handleExecuteSearch = () => {
    let queryObject = {};
    if (queryParameters.serialNumbers) {
      queryObject.serial_numbers = queryParameters.serialNumbers;
    }
    if (queryParameters.status) {
      queryObject.missing_status = queryParameters.status;
    }
    dispatch(getMissingSnsStatus(queryObject));
  };
  const handleExportMissingSnsStatus = (missingSnsStatus, format = "csv") => {
    const fields = [
      { name: "missing_sns_status_identifier" },
      { name: "serial_number" },
      { name: "created_day", value: "created_at" }, // Maps to created_at in data
      { name: "status", value: "sn_status" }, // Maps to sn_status in data
      { name: "part_number" },
      { name: "missing_detection_location", value: "tag" }, // Maps to tag in data
    ];

    if (format === "csv") {
      downloadCSV(fields, "missing_sns_status_export", ",", missingSnsStatus);
    } else {
      downloadJSON(fields, "missing_sns_status_export", missingSnsStatus);
    }
  };
  const handleExecuteBulkSearch = () => {
    let queryObject = {};
    if (queryParameters.serialNumbers) {
      queryObject.serial_numbers = queryParameters.serialNumbers;
    }
    if (queryParameters.status) {
      queryObject.missing_status = queryParameters.status;
    }
    queryObject.bulk = true;
    const saml_token = getLocalStorageSAMLToken();
    const infoToken = extractUserInfoFromToken(saml_token);
    queryObject.email = infoToken.email;
    queryObject.request_user = infoToken.username;
    dispatch(getMissingSnsStatusBulk(queryObject));
  };

  // Reset the URL processing flag when the URL actually changes
  useEffect(() => {
    hasProcessedUrlParams.current = false;
  }, [searchParams.toString()]);

  const handleDeleteSerialNumber = (chipToDelete) => () => {
    dispatch(
      removeParameter({ missingSnsStatus: { serialNumbers: [chipToDelete] } }),
    );
  };
  const handleClearSerialNumber = () => {
    dispatch(clearParameters({ missingSnsStatus: "serialNumbers" }));
  };
  const handleDeleteStatus = (chipToDelete) => () => {
    dispatch(removeParameter({ missingSnsStatus: { status: [chipToDelete] } }));
  };
  const handleClearStatus = () => {
    dispatch(clearParameters({ missingSnsStatus: "status" }));
  };

  const missingSnsStatus = useSelector((state) => {
    if (state.filteredMissingSnsStatus) {
      return state.filteredMissingSnsStatus.map((missing_sns_status) => {
        return {
          missing_sns_status_identifier:
            missing_sns_status?.PK + missing_sns_status?.SK,
          serial_number: missing_sns_status?.serial_number,
          created_at: missing_sns_status?.created_at,
          sn_status: missing_sns_status?.sn_status,
          device_type: missing_sns_status?.device_type,
          part_number: missing_sns_status?.part_number,
          tag: missing_sns_status?.tag,
        };
      });
    } else return [];
  });
  const [openBulkConfirmation, setOpenBulkConfirmation] = React.useState(false);

  const statusValues = [
    "MISSING",
    "Known in DatCol",
    "Not shipped to hub",
    "Unknown by ALE",
    "SN created",
  ];

  const handleKeydown = (event) => {
    if (event.key === "Enter") {
      event.stopPropagation();
      event.preventDefault();
      setTimeout(() => {
        handleExecuteSearch();
      }, 0);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);
    return () => {
      document.removeEventListener("keydown", handleKeydown);
    };
  }, []);

  return (
    <React.Fragment>
      <Dialog open={openBulkConfirmation} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body2">
            This query could take a few minutes, are you sure you want to
            continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              handleExecuteBulkSearch();
              setOpenBulkConfirmation(false);
            }}
          >
            Yes
          </Button>

          <Button variant="text" onClick={() => setOpenBulkConfirmation(false)}>
            No
          </Button>
        </DialogActions>
      </Dialog>
      <MultiValueInput
        setCurrentParam={setCurrentSerialNumber}
        setQueryParams={setParameters}
        currentParam={currentSerialNumber}
        queryParams={queryParameters?.serialNumbers}
        handleDelete={handleDeleteSerialNumber}
        handleClear={handleClearSerialNumber}
        label="Serial Number"
        family="missingSnsStatus"
        type="serialNumbers"
      />
      <MultiSelector
        setQueryParams={setParameters}
        queryParams={queryParameters?.status}
        handleDelete={handleDeleteStatus}
        handleClear={handleClearStatus}
        label="Status"
        family="missingSnsStatus"
        type="status"
        options={statusValues}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<MaterialSymbol name="search" size="button" />}
          onClick={handleExecuteSearch}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          startIcon={<MaterialSymbol name="cloud_download" size="button" />}
          onClick={() => setOpenBulkConfirmation(true)}
        >
          Bulk Export
        </Button>
      </Stack>
      {missingSnsStatus.length > 0 && (
        <DataTable
          title="Missing Serial Numbers Status"
          subtitle={
            missingSnsStatus.length === 100
              ? `${missingSnsStatus.length} results, use bulk export for more`
              : `${missingSnsStatus.length} results`
          }
          columns={MISSING_SNS_STATUS_COLUMNS}
          rows={missingSnsStatus}
          getRowId={(row, i) => `${row?.missing_sns_status_identifier}-${i}`}
          defaultRowsPerPage={25}
          size="small"
          actions={(rows) => (
            <ExportMenu
              disabled={!rows.length}
              onExport={(format) => handleExportMissingSnsStatus(rows, format)}
            />
          )}
          sx={{ mt: 3 }}
        />
      )}
    </React.Fragment>
  );
}
